import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  ApDynComponentComponent
}                                                                      from '../../ap-dyncomponent/ap-dyncomponent.component';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                               from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  FieldStore
}                                                               from '../../stores/farm/field.store';
import {
  FormStore, IEntryComponent
} from '../../stores/layout/form.store';
import {
  LanguageStore
}                                                               from '../../stores/translation/language.store';
import {
  SettingsStore
}                                                               from '../../stores/base-data/settings.store';
import {
  MapViewStore
}                                                               from '../../stores/layout/mapview.store';
import {
  DriverStore
}                                                               from '../../stores/docu/driver.store';
import {
  InstrumentStore
}                                                               from '../../stores/docu/instrument.store';
import {
  MachineStore
}                                                               from '../../stores/docu/machine.store';
import {GetRoundNumericPipe}                                    from '../../ap-utils';
import {
  IPpPlanningBookingGridItem
}                                                               from '../../plant-protection/types/pp-planning-booking.types';
import {
  PpConstants
}                                                               from './planning-wizard/pp-planning-wizard.types';
import {filter, map}                                            from 'rxjs/operators';
import {MapViewMode}                                            from '../../ap-interface';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                                               from '../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  nameOf
}                                                               from '../../ap-core/utils/ap-name-of';
import {
  ApDynGridGroupColumnConfig
}                                                               from '../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  GetRoundNumericService
}                                                               from '../../ap-utils/service/get-round-numeric.service';
import {
  ApTranslationService
}                                                               from '../../ap-utils/service/ap-translation.service';
import {
  OperationModesEnum
}                                                               from '../../stores/common/operation-modes.store';
import {
  ApDynGridPagerWizardButton,
  ApDynGridPagerWizardConfig,
  ApDynGridPagerWizardSection
}                                                               from '../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {
  ApUtilService
}                                                               from '../../ap-utils/service/ap-util.service';
import {
  ApDynGridStatusColumnConfig
}                                                               from '../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  PpCommonService
}                                                               from '../../plant-protection/services/pp-common.service';
import {
  ApNutrientService
}                                                               from '../../ap-utils/service/ap-nutrient.service';
import {
  FormatType
}                                                               from '../../ap-utils/types/ap-format-duration.types';
import {
  ApTooltipContentService
}                                                               from '../../ap-utils/service/ap-tooltip-content.service';
import {
  TimeFormatPipe
}                                                               from '../../ap-utils/pipes/ap-format-duration.pipe';
import {
  TimeAreaService
}                                                               from '../../services/data/time-area.service';
import ILanguage = Data.Language.ILanguage;
import {IPpPlanningShowGridItem}                                from './plan-show/pp-plan-show.types';
import {FarmCropV2CrStore}                                      from '../../stores/base-data/farm.cropv2cr.store';
import {CropNameV2CrService}                                    from '../../services/data/crop-namev2cr.service';

@Component({
    selector: 'pp-planning-show',
    template: `
    <ap-dyncomponent [loading$]="loading$"
                     [headerIcon]="'ap-fungidetect-icon'"
                     [columns]="columns$ | async"
                     [items]="items$ | async"
                     [pager]="pager">
    </ap-dyncomponent>
  `,
  standalone: false
})
export class PpPlanShowComponent implements OnInit, AfterViewInit, OnDestroy, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('Global__Show');
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  @Input() public data: { DataItem: IPpPlanningBookingGridItem, IsBooked: boolean };
  public columns$: Observable<ApDynGridColumnConfigBase[]> = of([]);
  public items$: Observable<IPpPlanningShowGridItem[]> = of([]);
  public loading$: Observable<boolean> = of(false);
  public pager: ApDynGridPagerConfigBase;
  private _subscriptions: Subscription[] = [];

  constructor(private farmCropV2CrStore: FarmCropV2CrStore,
              private cropNameV2CrService: CropNameV2CrService,
              private fieldStore: FieldStore,
              private formStore: FormStore,
              private languageStore: LanguageStore,
              private settingsStore: SettingsStore,
              private mapViewStore: MapViewStore,
              private roundNumericService: GetRoundNumericService,
              private translationService: ApTranslationService,
              private driverStore: DriverStore,
              private instrumentStore: InstrumentStore,
              private machineStore: MachineStore,
              private ppCommonService: PpCommonService,
              private timeAreaService: TimeAreaService,
              private nutrientService: ApNutrientService,
              private tooltipContentService: ApTooltipContentService,
              private timeFormatPipe: TimeFormatPipe,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  public ngOnInit(): void {
    this.loading$ = combineLatest([
      this.fieldStore.Listen(s => s.loading),
      this.farmCropV2CrStore.Listen(s => s.loading)
    ]).pipe(
      map((loadings) => loadings.some((l) => l))
    );
    this.pager = this._getPager();
    this.columns$ = this._buildGrid();
    this.items$ = this._buildDataItems();
  }

  public ngAfterViewInit(): void {
    this._subscriptions.push(
      this.dynComponent.SelectedItems$.subscribe((items: IPpPlanningShowGridItem[]) => {
        const fieldsToSelect: string[] = items.map(i => i.FieldId?.toString());
        if (fieldsToSelect?.length > 0) {
          this.fieldStore.changeSelectedField(fieldsToSelect);
        }
      })
    );
    this._subscriptions.push(
      this.items$.subscribe((items: IPpPlanningShowGridItem[]): void => {
        const itemsToSelect = items.map(i => i.Id);
        this.dynComponent.dynGrid.gridPaging.setSelectedKeys(itemsToSelect);
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Build overview data items
   */
  private _buildDataItems(): Observable<IPpPlanningShowGridItem[]> {
    if (!this.data || this.data?.DataItem?.PlanBooks?.length <= 0) {
      return of([]);
    }

    return combineLatest([
      this.farmCropV2CrStore.Listen(s => s.data),
      this.fieldStore.Listen(s => s.data)
    ]).pipe(
      filter(([farmCrops, fields]) =>
        !!farmCrops && !!fields),
      map(([_, fields]) => {
        const gridData = new Array<IPpPlanningShowGridItem>();
        const planBooks = this.data?.DataItem?.PlanBooks?.filter(p =>
          this.data?.IsBooked ? p.BookedAt !== null : p.BookedAt === null);
        for (const planBook of planBooks) {
          if (planBook == null) {
            continue;
          }
          const areaHa = planBook.AreaHa;
          const field = fields.FirstOrDefault(f => f.Id === planBook.FieldId);
          const haPerHours = this.timeAreaService.getHaPerHoursFromTotalMilliseconds(areaHa,
            planBook.Duration?.TotalMilliseconds);
          gridData.push({
            Id: planBook?.Id,
            FieldId: field?.Id,
            Status: this.nutrientService.getFertilizerStatus(planBook.ApplicationMode, this.data?.IsBooked)?.state,
            StatusClass: this.nutrientService.getFertilizerStatus(planBook.ApplicationMode, this.data?.IsBooked)?.class,
            StatusDesc: this.nutrientService.getFertilizerStatusDescription(planBook.ApplicationMode, this.data?.IsBooked),
            Nr: field?.FieldNumber,
            TNr: field?.FieldSubnumber,
            NrTNr: field != null ? this.fieldStore.getFieldNumberAndSubNumber(field) : '',
            Name: field?.FieldName,
            Area: areaHa,
            Crop: this.cropNameV2CrService.getCropNameById(planBook.FarmCropId),
            ECStage: planBook.EcValue,
            ApplicationDate: new Date(planBook.ApplicationDate),
            DurationInMilliseconds: planBook.Duration?.TotalMilliseconds,
            HaPerHour: haPerHours,
            WaterAvg: planBook.ApplicationRate,
            WaterMax: planBook.ApplicationMax,
            WaterMin: planBook.ApplicationMin,
            Machine: this.machineStore.getMachineById(planBook.ExportMachine)?.NameWithSign,
            Instrument: this.instrumentStore.getInstrumentById(planBook.ExportInstrument)?.Name,
            Driver: this.driverStore.getDriverById(planBook.ExportDriver)?.FirstNameFullName,
            Products: this.ppCommonService.getAppliedProductsExpr(planBook.ProductList), // selectedProducts?.map(p => this.buildProductExpr(p))?.join(', '),
            ProductsTooltip: this.ppCommonService.getAppliedProductsExpr(planBook.ProductList, true), // this.ppCommonService.getAppliedProductsExpr(selectedProducts),
            ControlFactorExpr: this.translationService.translate(PpConstants.PpControlFactors.FirstOrDefault(c => c.Id === planBook.Slope)?.Key)
          });
        }
        return gridData;
      }));
  }

  private _buildGrid(): Observable<ApDynGridColumnConfigBase[]> {
    return combineLatest([
      this.languageStore.SelectedLanguage$,
      this.settingsStore.FirstSetting$
    ]).pipe(
      filter(([language, setting]) => !!language && !!setting),
      map(([language, _]) => {
        const mapHide = this.mapViewStore.Listen(s => s.mode).pipe(
          map((mapMode) => mapMode === MapViewMode.HIDE));
        const isWorkTypeSpraying = this.ppCommonService.isWorkTypeSpraying(this.data?.DataItem?.PlanBooks?.FirstOrDefault());
        const showPpPlanningColumns: ApDynGridColumnConfigBase[] = this._getCommonColumns(mapHide);
        switch (this.data?.DataItem?.OperationModeKey) {
          case OperationModesEnum.Constant:
            if (isWorkTypeSpraying) {
              showPpPlanningColumns.Add(this._getUniformSprayingColumns());
            }
            break;
          case OperationModesEnum.PpTarget:
            showPpPlanningColumns.AddRange(this._getTargetRateColumns());
            break;
        }
        showPpPlanningColumns.AddRange(this._getCommonEndColumns(language));
        return showPpPlanningColumns;
      }));
  }

  /**
   * Creates grid column configs for uniform operationMode with work type 'spraying'
   * @private
   */
  private _getUniformSprayingColumns(): ApDynGridColumnConfigBase {
    return new ApDynGridPropertyColumnConfig({
      title: `${this.translationService.translate('Global__AvgShort')} ${this.translationService.translate('Global__Water')} [${this.translationService.translate('Unit_l_per_ha')}]`,
      field: `${nameOf<IPpPlanningShowGridItem>('WaterAvg')}`,
      width: 100,
      filterable: true,
      headerFilterable: true,
      cssClass: 'right',
      filterType: FilterType.NUMBER,
    });
  }

  /**
   * Creates grid column configs for target rate operationMode
   * @private
   */
  private _getTargetRateColumns(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'RateSlope',
        field: `${nameOf<IPpPlanningShowGridItem>('ControlFactorExpr')}`,
        hide: true,
        width: 80
      }),
      new ApDynGridGroupColumnConfig({
        title: `${this.translationService.translate('Global__Water')} [${this.translationService.translate('Unit_l_per_ha')}]`,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'Global__AvgShort',
            field: `${nameOf<IPpPlanningShowGridItem>('WaterAvg')}`,
            width: 50,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: `${nameOf<IPpPlanningShowGridItem>('WaterMin')}`,
            width: 50,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: `${nameOf<IPpPlanningShowGridItem>('WaterMax')}`,
            width: 50,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          })
        ]
      })
    ];
  }

  /**
   * Creates grid column configs which are common for all operation modes.
   * @param mapHide
   * @private
   */
  private _getCommonColumns(mapHide: Observable<boolean>): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: nameOf<IPpPlanningShowGridItem>('Status'),
        class: nameOf<IPpPlanningShowGridItem>('StatusClass'),
        description: nameOf<IPpPlanningShowGridItem>('StatusDesc'),
        title: '',
        filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Number',
        field: `${nameOf<IPpPlanningShowGridItem>('Nr')}`,
        width: 40,
        hide: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini_Subnumber',
        field: `${nameOf<IPpPlanningShowGridItem>('TNr')}`,
        width: 40,
        hide: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Number',
        field: `${nameOf<IPpPlanningShowGridItem>('NrTNr')}`,
        width: 50,
        headerFilterable: true,
        hide: new ApDynGridColumnHideConfig({
          selfHide: mapHide
        })
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Name',
        field: `${nameOf<IPpPlanningShowGridItem>('Name')}`,
        width: 110,
        cssClass: 'left',
        sortIndex: 0,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Area',
        field: `${nameOf<IPpPlanningShowGridItem>('Area')}`,
        width: 50,
        headerFilterable: true,
        tooltip: (dataItem) => this.roundNumericService.stringify((dataItem as IPpPlanningShowGridItem)?.Area, 4),
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [this.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Crop',
        field: `${nameOf<IPpPlanningShowGridItem>('Crop')}`,
        width: 80,
        hide: true,
        cropColoring: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__EC',
        field: `${nameOf<IPpPlanningShowGridItem>('ECStage')}`,
        width: 50,
        hide: true,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
      })
    ];
  }

  /**
   * Creates grid column configs for the common last columns
   * @private
   */
  private _getCommonEndColumns(language: ILanguage): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Products',
        field: nameOf<IPpPlanningShowGridItem>('Products'),
        width: 200,
        tooltip: (dataItem) => (dataItem as IPpPlanningShowGridItem)?.ProductsTooltip,
        multiline: true,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Base__SortItemDate',
        field: `${nameOf<IPpPlanningShowGridItem>('ApplicationDate')}`,
        format: this.languageStore?.CurrentDateFormat,
        hide: true,
        width: 70
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPpPlanningShowGridItem>('DurationInMilliseconds'),
        title: `${this.translationService.translate('TotalTime')} [${this.translationService.translate('Base__UnitH')}]`,
        hide: true,
        width: 70,
        cssClass: 'right',
        headerFilterable: true,
        tooltip: (dataItem: IPpPlanningShowGridItem) => this.tooltipContentService.getCapacityPerformanceTooltip(dataItem.HaPerHour),
        pipes: [{
          pipe: this.timeFormatPipe,
          args: [{
            formatType: FormatType.Decimals,
            suffixValue: 'Base__UnitH',
            decimalsFormatOptions: {
              decimalsCount: 1,
              decimalsSeparator: language.DecimalSeperator
            }
          }]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Settings__Lbl_Menu_Resources',
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IPpPlanningShowGridItem>('Machine'),
            title: 'Base__Engine',
            width: 120,
            hide: true,
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IPpPlanningShowGridItem>('Instrument'),
            title: 'AgriConnect_Ini__Tool',
            width: 100,
            hide: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IPpPlanningShowGridItem>('Driver'),
            title: 'Docu_Ini__Motorist',
            width: 100,
            hide: true
          })
        ]
      })
    ];
  }

  private _getPager(): ApDynGridPagerWizardConfig {
    return new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardButton('Global_Back_With_Arrow', ApUtilService.createEventEmitter(this.cancel.bind(this), this._subscriptions)),
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardSection(),
      {
        formConfig: undefined
      }
    );
  }

  private _onClose(): void {
    this.formStore.closeForm();
  }

  private cancel() {
    this._onClose();
    this.formStore.navigateToParamUrl();
  }
}
